import React, { useState } from "react";
import InputField from "../../input";
import Checkbox from "../../checkbox";
import Button from "../../button";
import BackBtnGray from "../../svg/back-btn-gray";
import ArrowRight from "../../svg/arrow-right";
import User from "../../svg/user";
import PhoneDef from "../../svg/phone-def";
import EmailLight from "../../svg/email-light";
import Loader from "../../svg/loader";

interface Step2Props {
    onSubmit: (data: any) => void;
    onPrev: () => void;
    initialData: any;
    isLoading?: boolean;
    lang?: string; 
    id?: string;
}

const Step2: React.FC<Step2Props> = ({ onSubmit, onPrev, initialData, isLoading = false, lang, id }) => {
    const [data, setData] = useState({
        fullName: initialData.fullName || "",
        phone: initialData.phone || "",
        email: initialData.email || "",
        comments: initialData.comments || "",
        consent: initialData.consent || false,
    });

    const [errors, setErrors] = useState({
        fullName: false,
        phone: false,
        email: false,
        emailFormat: false, 
        consent: false,
    });

    const validateEmail = (email: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleSubmit = () => {
        const isEmailValid = validateEmail(data.email);

        const newErrors = {
            fullName: !data.fullName.trim(),
            phone: !data.phone.trim(),
            email: !data.email.trim(),
            emailFormat: !isEmailValid, 
            consent: !data.consent, 
        };

        setErrors(newErrors);

        if (!Object.values(newErrors).some((error) => error)) {
            onSubmit(data);
        }
    };

    const sendBtnTExt = lang === 'en' ? 'request a price quote' : 'Fa prisoffert';
    const processText = lang === 'en' ? 'Processing...' : 'Bearbetar...';
    const requredText = lang === 'en' ? 'This field is required' : 'Detta fält är obligatoriskt';

    return (
        <div id={id} className="rounded-[40px] p-[48px] shadow-[0_10px_24px_0_#00000026] max-[576px]:p-[20px] max-[576px]:rounded-[20px]">
            <h2 className="text-[32px] font-bold text-violet-500 mb-[40px] max-[576px]:text-[20px] max-[576px]:leading-[24px] max-[576px]:mb-[24px] ">
                {lang === 'en' ? 'Please fill in the required information' : 'Vänligen fyll i nödvändig information'}
            </h2>

            <div className="space-y-[32px]">
                <InputField
                    label={lang === 'en' ? 'Full Name' : 'Fullständigt namn'}
                    placeholder={lang === 'en' ? 'Write your full name' : 'Fullständigt namn'}
                    iconLeft={<User />}
                    value={data.fullName}
                    onChange={(e) => setData({ ...data, fullName: e.target.value })}
                    errorMessage={errors.fullName ? requredText : ""}
                />

                <div className="grid grid-cols-2 gap-4 max-[576px]:grid-cols-1">
                    <InputField
                        label={lang === 'en' ? 'Phone Number' : 'telefonnummer'}
                        placeholder={lang === 'en' ? 'Write your phone number' : 'Skriv ditt telefonnummer'}
                        iconLeft={<PhoneDef />}
                        type="number"
                        value={data.phone}
                        onChange={(e) => setData({ ...data, phone: e.target.value })}
                        errorMessage={errors.phone ? requredText : ""}
                    />
                    <InputField
                        label={lang === 'en' ? 'E-mail' : 'e-post'}
                        placeholder={lang === 'en' ? 'Write your e-mail' : 'Skriv din e-post'}
                        type="email"
                        iconLeft={<EmailLight />}
                        value={data.email}
                        onChange={(e) => setData({ ...data, email: e.target.value })}
                        errorMessage={
                            errors.email
                                ? requredText
                                : errors.emailFormat
                                ? lang === 'en' ? 'Please enter a valid email address' : 'Ange en giltig e-postadress'
                                : ""
                        }
                    />
                </div>

                <InputField
                    label= {lang === 'en' ? 'Additional Comments (Optional)' : 'ytterligare kommentarer (valfritt)'}
                    value={data.comments}
                    type="textarea"
                    placeholder={lang === 'en' ? 'Write text' : 'Skriv text'}
                    onChange={(e) => setData({ ...data, comments: e.target.value })}
                />
            </div>

            <div className="mt-[24px]">
                <Checkbox
                    label={lang === 'en' ? 'I consent to my personal data being processed in accordance with the ' : 'Jag samtycker till att mina personuppgifter behandlas i enlighet med'}
                    required={true}
                    checked={data.consent}
                    onChange={(checked) => setData({ ...data, consent: checked })}
                    link={`${lang === 'en' ? '/en' : ''}/integritetspolicy`}
                    linktext={lang === 'en' ? 'privacy policy' : ' integritetspolicyn'}
                />
                {errors.consent && <span className="text-red-500 text-sm">{lang === 'en' ? 'You must agree to continue' : 'Du måste godkänna att fortsätta'}</span>}
            </div>

            <div className="flex justify-between mt-6 max-[767px]:flex-wrap max-[767px]:gap-[24px]  max-[767px]:justify-center">
                <Button 
                    onClick={onPrev}
                    text={lang === 'en' ? 'Back' : 'tillbaka'}
                    type="secondary" 
                    icon={<BackBtnGray />}
                    size="big"
                />
                <Button 
                    onClick={handleSubmit} 
                    mobileFull="yes"
                    type="primary" 
                    iconPostiton="right"
                    text={isLoading ? processText : sendBtnTExt} 
                    icon={isLoading ? <Loader /> : <ArrowRight />} 
                />
            </div>
        </div>
    );
};

export default Step2;